import {
    addFollow,
    FollowButton,
    withFollow,
} from '@eventbrite/follow-organizer-ebui';
import { logEvent } from '@eventbrite/statsig';
import React, { useContext } from 'react';
import {
    ORGANIZER_FOLLOW_CLICK,
    ORGANIZER_PROFILE_PAGE_AREA,
    ORGANIZER_UNFOLLOW_CLICK,
} from '../constants';
import { EnvContext, UserContext } from '../context';
import { Organizer } from '../types';
import './FollowOrganizer.scss';

interface FollowOrganizerWidgetProps {
    gaSettings?: object; // Unused by widget, required by addFollow
    gaCategory?: string; // Unused by widget, required by addFollow
    isAuthenticated?: boolean; // Unused by widget, required by addFollow
    loginOptions?: object; // Unused by widget, required by addFollow
    organizers: string[]; // Array of organizer IDs - only organizers[0] is used
    size?: string;
    __containerClassName?: string;
    renderFollowCount?: (
        followCount: string,
    ) => React.ReactNode | JSX.Element | null;
}

const EnhancedFollowButton = withFollow(FollowButton);
const FollowOrganizerWidget: React.FunctionComponent<
    FollowOrganizerWidgetProps
> = (props) => {
    return (
        <EnhancedFollowButton
            size={props.size}
            isOnlyButton={true}
            organizerId={props.organizers[0]}
            renderFollowCount={props.renderFollowCount}
            __containerClassName={props.__containerClassName}
            onFollow={() => {
                logEvent(ORGANIZER_FOLLOW_CLICK);
            }}
            onUnFollow={() => {
                logEvent(ORGANIZER_UNFOLLOW_CLICK);
            }}
        />
    );
};

interface FollowOrganizerProps {
    organizer: Organizer;
    size?: string;
    containerClassName?: string;
    userId: string;
    renderFollowCount?: (
        followCount: string,
    ) => React.ReactNode | JSX.Element | null;
    /**
     * If true, uses shared context from FollowProvider instead of creating own addFollow context.
     * Set to true when FollowProvider is wrapping this component.
     */
    useSharedContext?: boolean;
}

const EnhancedFollowOrganizerWidget = addFollow(FollowOrganizerWidget);

/**
 * FollowOrganizer - Uses addFollow(withFollow(button)) structure.
 *
 * When useSharedContext=true: Uses withFollow(button) directly, consuming shared context from FollowProvider.
 * When useSharedContext=false (default): Wraps with addFollow, creating its own context.
 *
 * This maintains the existing addFollow logic (login handling, cookies, etc.)
 * while allowing multiple instances to sync when wrapped in FollowProvider.
 */
export const FollowOrganizer: React.SFC<FollowOrganizerProps> = (props) => {
    const { isAuthenticated } = useContext(UserContext);
    const { gaSettings } = useContext(EnvContext);
    const userId = props.userId;
    const loginOptions = {
        preventShowingLoginModal: true,
    };

    // If using shared context, render FollowOrganizerWidget directly (withFollow(button))
    // It will consume context from FollowProvider's addFollow wrapper
    if (props.useSharedContext) {
        return (
            <FollowOrganizerWidget
                organizers={[props.organizer.id]}
                size={props.size}
                __containerClassName={props.containerClassName}
                renderFollowCount={props.renderFollowCount}
            />
        );
    }

    // Otherwise, wrap with addFollow to create own context (standalone mode)
    return (
        <EnhancedFollowOrganizerWidget
            isAuthenticated={isAuthenticated}
            gaSettings={gaSettings}
            gaCategory={'organizer'}
            organizers={[props.organizer.id]}
            renderFollowCount={props.renderFollowCount}
            loginOptions={loginOptions}
            size={props.size}
            userId={userId}
            __containerClassName={`${props.containerClassName}`}
            pageArea={ORGANIZER_PROFILE_PAGE_AREA}
        />
    );
};
